<template>
  <ReportPageLayout>
    <v-container class="px-4">
      <v-responsive max-width="1320" class="mx-auto">
        <ReportsHeader
          :total="reportsStore.totalItems"
          @sort-change="handleSortChange"
        />
        <v-row>
          <v-col cols="12" md="2" class="pr-md-1 reports-sidebar-column">
            <ReportsSidebar />
          </v-col>

          <v-col cols="12" md="10" class="reports-content-column">
            <ReportsCardsList />
          </v-col>
        </v-row>
      </v-responsive>
    </v-container>
    <!-- Compare bar temporarily removed -->
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { useReportsStore } from '../store';
  import { onMounted, watch } from 'vue';
  import { useRouter, useRoute } from 'vue-router';

  // Import reports components
  import ReportsHeader from '../components/reports/ReportsHeader.vue';
  import ReportsSidebar from '../components/reports/ReportsSidebar.vue';
  import ReportsCardsList from '../components/reports/ReportsCardsList.vue';

  // Import other feature components
  import ReportPageLayout from '../layouts/ReportPageLayout.vue';

  // Define props for URL query parameters
  interface Props {
    subject?: string;
    formats?: string[];
    grades?: string[];
    alignment?: string[];
    usability?: string[];
    versions?: string[];
    publishers?: string[];
    supportingDocuments?: string[];
    page?: number;
    order?: string;
    dir?: string;
    search?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    formats: () => [],
    grades: () => [],
    alignment: () => [],
    usability: () => [],
    versions: () => [],
    publishers: () => [],
    supportingDocuments: () => [],
    page: 1,
  });

  const reportsStore = useReportsStore();
  const router = useRouter();
  const route = useRoute();

  // Function to update URL with current state
  const updateRoute = () => {
    const query: Record<string, any> = {};

    // Add filter query parameters
    if (reportsStore.selectedFormats.length > 0) {
      query.formats = reportsStore.selectedFormats;
    }
    if (reportsStore.selectedGrades.length > 0) {
      query.grades = reportsStore.selectedGrades;
    }
    if (reportsStore.selectedAlignment.length > 0) {
      query.alignment = reportsStore.selectedAlignment;
    }
    if (reportsStore.selectedUsability.length > 0) {
      query.usability = reportsStore.selectedUsability;
    }
    if (reportsStore.selectedVersions.length > 0) {
      query.versions = reportsStore.selectedVersions;
    }
    if (reportsStore.selectedPublishers.length > 0) {
      query.publishers = reportsStore.selectedPublishers;
    }
    if (reportsStore.selectedSupportingDocuments.length > 0) {
      query.supportingDocuments = reportsStore.selectedSupportingDocuments;
    }

    // Add pagination
    if (reportsStore.currentPage > 1) {
      query.p = reportsStore.currentPage;
    }

    // Add sorting
    const sortOptionMap: Record<string, { order: string; dir: string }> = {
      'Recently Published': { order: 'published', dir: 'desc' },
      Alphabetical: { order: 'alpha', dir: 'asc' },
      Copyright: { order: 'edition', dir: 'desc' },
    };

    const sortParams = sortOptionMap[reportsStore.sortOption];
    if (sortParams && reportsStore.sortOption !== 'Recently Published') {
      query.order = sortParams.order;
      query.dir = sortParams.dir;
    }

    // Add search
    if (reportsStore.searchQuery.trim()) {
      query.search = reportsStore.searchQuery.trim();
    }

    // Determine the path
    let path = '/reports';
    if (reportsStore.selectedSubjects.length > 0) {
      path += `/${reportsStore.selectedSubjects[0].toLowerCase()}`;
    }

    // Only update if the route has actually changed
    const newRoute = { path, query };
    if (
      JSON.stringify(newRoute) !==
      JSON.stringify({ path: route.path, query: route.query })
    ) {
      router.replace(newRoute);
    }
  };

  // Watch for changes that should update the URL
  watch(
    () => reportsStore.currentPage,
    () => updateRoute(),
    { flush: 'post' },
  );

  watch(
    () => reportsStore.sortOption,
    () => updateRoute(),
    { flush: 'post' },
  );

  watch(
    () => reportsStore.searchQuery,
    () => updateRoute(),
    { flush: 'post' },
  );

  // Watch for filter changes
  watch(
    () => [
      reportsStore.selectedFormats,
      reportsStore.selectedGrades,
      reportsStore.selectedAlignment,
      reportsStore.selectedUsability,
      reportsStore.selectedVersions,
      reportsStore.selectedPublishers,
      reportsStore.selectedSupportingDocuments,
      reportsStore.selectedSubjects,
    ],
    () => updateRoute(),
    { deep: true, flush: 'post' },
  );

  onMounted(() => {
    // Initialize store with URL query parameters
    if (props.subject) {
      reportsStore.setSubject(props.subject, true);
    }
    if (props.formats.length > 0) {
      props.formats.forEach((format) => reportsStore.setFormat(format, true));
    }
    if (props.grades.length > 0) {
      props.grades.forEach((grade) => reportsStore.setGrade(grade, true));
    }
    if (props.alignment.length > 0) {
      props.alignment.forEach((alignment) =>
        reportsStore.setAlignment(alignment, true),
      );
    }
    if (props.usability.length > 0) {
      props.usability.forEach((usability) =>
        reportsStore.setUsability(usability, true),
      );
    }
    if (props.versions.length > 0) {
      props.versions.forEach((version) =>
        reportsStore.setVersion(version, true),
      );
    }
    if (props.publishers.length > 0) {
      props.publishers.forEach((publisher) =>
        reportsStore.setPublisher?.(publisher, true),
      );
    }
    if (props.supportingDocuments.length > 0) {
      props.supportingDocuments.forEach((doc) =>
        reportsStore.setSupportingDocument?.(doc, true),
      );
    }

    // Handle pagination and sorting from URL
    if (props.page && props.page !== reportsStore.currentPage) {
      reportsStore.currentPage = props.page;
    }

    // Handle sorting from URL
    if (props.order && props.dir) {
      const sortOptionMap: Record<string, string> = {
        published: 'Recently Published',
        alpha: 'Alphabetical',
        edition: 'Copyright',
      };
      const sortOption = sortOptionMap[props.order];
      if (sortOption && sortOption !== reportsStore.sortOption) {
        reportsStore.sortOption = sortOption;
      }
    }

    // Handle search from URL
    if (props.search && props.search !== reportsStore.searchQuery) {
      reportsStore.setSearchQuery(props.search, false); // false to not trigger immediate fetch
    }

    // Data fetching is now primarily handled by store.initialize()
    // This component can observe store state (loading, error, reports)
    // and pass necessary props to children.
    // If filters are needed for ReportsSidebar and aren't loaded by initialize,
    // a targeted fetchFilters could still be here, but initialize should cover it.
    if (
      reportsStore.filters.length === 0 &&
      !reportsStore.loading &&
      !reportsStore.filtersRequestInProgress
    ) {
      reportsStore.fetchFilters(); // Fetch if still missing after potential init
    }
  });

  function handleSortChange(option: string) {
    reportsStore.setSortOption(option);
  }
</script>

<style scoped>
  .v-responsive {
    padding-right: 0;
  }

  /* Enhanced sidebar column specificity for production environment */
  .reports-sidebar-column.v-col {
    padding-right: 8px !important;
  }

  @media (max-width: 959px) {
    .reports-sidebar-column.v-col {
      padding-right: 12px !important;
      margin-bottom: 16px !important;
    }
  }

  .reports-content-column.v-col {
    padding-left: 8px !important;
  }

  @media (max-width: 959px) {
    .reports-content-column.v-col {
      padding-left: 12px !important;
    }
  }

  /* Ensure proper flex behavior for the row */
  .v-row {
    margin: 0 !important;
  }

  .v-row .v-col {
    box-sizing: border-box !important;
  }
</style>
